import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Manages all wizard state for the Task Guide dialog.
 * Keeps track of the current step, user selections, and navigation.
 */
const CAPACITY_OPTIONS = {
  single: { targets: 1 },
  few: { targets: 3 },
  many: { targets: 5 },
};

const useTaskGuideState = () => {
  const tasks = useSelector((state) => state.tasks.taskSet);
  const activeTasks = useMemo(
    () => Object.values(tasks).filter((t) => !t.completed),
    [tasks],
  );
  const taskCount = activeTasks.length;
  const untargetedTasks = useMemo(
    () => activeTasks.filter((t) => !t.targeted),
    [activeTasks],
  );
  const allTargeted = taskCount > 0 && untargetedTasks.length === 0;

  // Step management
  const [currentStep, setCurrentStep] = useState(null); // set on open
  const [capacity, setCapacity] = useState(null); // 'single' | 'few' | 'many'
  const [approach, setApproach] = useState(null); // 'adaptive' | 'priority'
  const [mood, setMood] = useState(null); // 1-5 card index
  const [energy, setEnergy] = useState(null); // 1-5 card index
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [taskPage, setTaskPage] = useState(0);
  const [mindsetSaving, setMindsetSaving] = useState(false);
  const [firstActionText, setFirstActionText] = useState("");
  const [chosenFirstTask, setChosenFirstTask] = useState(null);

  // Determine the starting step based on task count
  const getStartingStep = useCallback(() => {
    if (taskCount === 0) return "no-tasks";
    if (allTargeted) return "all-targeted";
    if (taskCount === 1) return "single-task";
    return "capacity";
  }, [taskCount, allTargeted]);

  // Initialize / reset the wizard
  const initialize = useCallback(
    (resumeStep) => {
      if (resumeStep) {
        setCurrentStep(resumeStep);
      } else {
        setCurrentStep(getStartingStep());
      }
    },
    [getStartingStep],
  );

  const reset = useCallback(() => {
    setCurrentStep(null);
    setCapacity(null);
    setApproach(null);
    setMood(null);
    setEnergy(null);
    setSelectedTargets([]);
    setTaskPage(0);
    setMindsetSaving(false);
    setFirstActionText("");
    setChosenFirstTask(null);
  }, []);

  // Map 1-5 card selections to 1-10 mindset scale
  const mapToMindsetScale = useCallback((value) => {
    const mapping = { 1: 1, 2: 3, 3: 5, 4: 8, 5: 10 };
    return mapping[value] || 5;
  }, []);

  // Composite mood/energy score for determining number of displayed tasks
  const getDisplayTaskCount = useCallback(() => {
    if (mood === null || energy === null) return 5;
    const composite = (mood + energy) / 2;
    if (composite <= 2) return 3;
    if (composite <= 3.5) return 5;
    return 7;
  }, [mood, energy]);

  // Get suggested target count from capacity, capped to available untargeted task count
  const getSuggestedTargetCount = useCallback(() => {
    const suggested = capacity ? CAPACITY_OPTIONS[capacity]?.targets || 3 : 3;
    return Math.min(suggested, untargetedTasks.length);
  }, [capacity, untargetedTasks.length]);

  // Toggle a task in/out of targets
  const toggleTarget = useCallback((taskId) => {
    setSelectedTargets((prev) => {
      if (prev.includes(taskId)) {
        return prev.filter((id) => id !== taskId);
      }
      return [...prev, taskId];
    });
  }, []);

  return {
    // Task data
    activeTasks,
    untargetedTasks,
    allTargeted,
    taskCount,
    // Step
    currentStep,
    setCurrentStep,
    initialize,
    reset,
    // Selections
    capacity,
    setCapacity,
    approach,
    setApproach,
    mood,
    setMood,
    energy,
    setEnergy,
    selectedTargets,
    setSelectedTargets,
    toggleTarget,
    taskPage,
    setTaskPage,
    mindsetSaving,
    setMindsetSaving,
    firstActionText,
    setFirstActionText,
    chosenFirstTask,
    setChosenFirstTask,
    // Computed
    mapToMindsetScale,
    getDisplayTaskCount,
    getSuggestedTargetCount,
  };
};

export default useTaskGuideState;
